import { useMe } from "@nu-assist/shared-fe";
import { CheckCircle2, Compass, Send, Settings, Trophy, Users } from "lucide-react";
import { useIsAdmin } from "../../shared/useIsAdmin";
import { StaffAlertsWidget } from "./StaffAlertsWidget";
/**
 * /dashboard — the home view, rendered inside `WebShellLayout` (which
 * provides AuthGuard + the web app-shell). This component is now
 * content-only: the welcome heading + the lifecycle home grid, and under
 * them the self-expiring "What's new" carousel (`WhatsNewCarousel`).
 *
 * The six lifecycle cards are filtered by `useFeatureAccess`: a normal
 * user sees only the cards whose section has a released page; `?preview=1`
 * reveals all six with a `coming soon` badge.
 *
 * **Routing rule (PR #241 — dashboard card routing fix):** every card's
 * click target is resolved dynamically from `firstReleasedPageHref(card.
 * sectionId, activeFlags())` — i.e. it points at the first page in the
 * section (per `leftMenuModel` order) whose flag is on for the current
 * stage. This replaces the previous hardcoded `path` per card, which
 * dead-clicked or landed on the wrong page whenever the per-stage flag
 * registry shifted under the hardcode (`my-imports` and `dash-company`
 * are OFF on dev → cards routed straight through `FeatureRoute` back to
 * `/dashboard`; Closing pointed at `closing-email-template` instead of
 * the canonical `closing-email`). A card whose helper returns `null` is
 * rendered disabled (greyed, `aria-disabled`, no click handler) instead
 * of a dead click — the visual hierarchy of "this section is on the
 * roadmap but nothing's shipped yet" is preserved without breaking the
 * user's mental model of "card click → real page". Sidebar auto-expands
 * the destination section via `Section.holdsActive`/`useEffect` in
 * `WebLeftMenu`, so no extra coordination is needed.
 *
 * Identity (`useMe`) is read for the welcome name. AuthGuard (in the
 * layout) guarantees an authenticated session by render time. The
 * impersonation banner now lives at the shell level (see
 * `features/ImpersonationBannerSlot`) so it appears on every route.
 *
 * Tokenized: token color utilities + `text-*` roles + Tailwind's 4px
 * scale only. No inline styles, no arbitrary `text-[Npx]`.
 */
import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { activeFlags } from "../../config/featureFlags";
import { firstReleasedPageHref, useFeatureAccess } from "../../features/useFeatureAccess";
import { DashboardCard, DashboardGrid } from "./DashboardGrid";
import { MyOpenSearchesWidget } from "./MyOpenSearchesWidget";
import { WhatsNewCarousel } from "./WhatsNewCarousel";

// Static home-grid cards, derived 1:1 from v37 `.home-grid` (six cards —
// one per content-bearing lifecycle stage). Each card carries the
// `sectionId` it represents (drives visibility + the dynamic href). The
// click target is RESOLVED at render time via `firstReleasedPageHref`
// rather than hardcoded — see the module docstring for the rationale.
interface HomeCard {
  sectionId: string;
  /**
   * External-link shortcut: when set, the card opens this URL in a new tab
   * instead of resolving an in-app href. Mirrors the left-menu
   * `externalUrl` treatment so both entry points for a shortcut go to the
   * same place. No active in-app card uses this today; kept so a future
   * external-only stage card (e.g. an n8n form like AgendaGPT once was)
   * can be wired without rebuilding the contract.
   */
  externalUrl?: string;
  title: string;
  icon: ReactNode;
}

/**
 * Home-grid cards, resolved at RENDER time rather than module scope
 * (2026-09-02, PR #821 review iter-1, Agent C finding 1). The reason was the
 * My Searches card title, which branched on `resolveStage()` while the
 * production freeze held: a module-scope literal bakes the env read into
 * IMPORT time, where `vi.stubEnv` is inert, so the prod variant was
 * untestable (the dev test passed only because the test env defaults to dev).
 *
 * That branch is gone since 2026-09-23 (the card reads "My Searches." on every
 * stage), but the function shape stays: six tiny objects per render is free,
 * the stage cannot change within a session so render-time resolution changes
 * no observable behavior, and this is the shape any FUTURE stage-dependent
 * card must take to be testable at all.
 */
function homeCards(): HomeCard[] {
  return [
    {
      sectionId: "winning",
      title: "Winning.",
      icon: <Trophy size={40} aria-hidden="true" />,
    },
    {
      sectionId: "linkedin",
      title: "Research & Sourcing.",
      icon: <Compass size={40} aria-hidden="true" />,
    },
    {
      sectionId: "sequence",
      title: "Outreach.",
      icon: <Send size={40} aria-hidden="true" />,
    },
    {
      sectionId: "client-engagement",
      // Third name for one card, so the trail is written down here rather than
      // left to git archaeology: "Clients Management." → "Searches."
      // (2026-08-07, PR #640, matching the sidebar section rename) → "My
      // Searches." (2026-08-10, Jose). Each rename chased the same drift — the
      // card's words describing a CLIENT-management surface that never existed,
      // while the page it opens is the recruiter's own search list.
      //
      // "My Searches." now names the page the card actually opens. That is not
      // a coincidence this PR arranged: `firstReleasedPageHref` walks
      // `leftMenuModel` order, `dash-searches` LEADS the section (PR #640
      // demoted Agenda GPT to last), and this release flips `dash-searches`
      // on — so the card resolves to `/client-engagement/searches` by the
      // ladder's own rules. No per-card routing override exists or is needed;
      // the destination is pinned by test in `Dashboard.test.tsx` instead.
      //
      // The `sectionId` deliberately stays `client-engagement`. It keys the
      // `featureFlags` registry, every `/client-engagement/*` route path and
      // `PHASE_PAGE_IDS`; renaming it would ripple through all three to change
      // something no user can see. Same label/id split the section itself
      // documents in `leftMenuModel.ts`.
      // One name on every stage again since 2026-09-23. The 2026-09-02
      // production freeze had this branch on `resolveStage()` — the prod build
      // kept "Client Engagement." because `dash-searches` was dark there, so the
      // card resolved to Agenda GPT and "My Searches." would have promised a
      // page the recruiter could not open. `dash-searches` shipped to prod with
      // the delivery, so the promise is good everywhere; the branch went with
      // the matching one in `leftMenuModel.ts`.
      title: "My Searches.",
      icon: <Users size={40} aria-hidden="true" />,
    },
    {
      sectionId: "closing",
      title: "Closing & Post-Close.",
      icon: <CheckCircle2 size={40} aria-hidden="true" />,
    },
    {
      sectionId: "dashboards",
      title: "Operations.",
      icon: <Settings size={40} aria-hidden="true" />,
    },
  ];
}

export function Dashboard() {
  const me = useMe();
  const navigate = useNavigate();
  const access = useFeatureAccess();
  const { isAdmin } = useIsAdmin();
  // Snapshot the active stage flags once per render — `firstReleasedPageHref`
  // is pure over this object, so each card resolves its own href against the
  // same registry without re-reading the env per call.
  const flags = activeFlags();

  // AuthGuard guarantees authenticated state by render time; defensive
  // narrow for TS.
  if (me.status !== "authenticated") return null;

  const fullName =
    [me.data.first_name, me.data.last_name].filter(Boolean).join(" ") || me.data.email;

  // The widget is a shortcut INTO My Searches, so it lives and dies with that
  // page's own access. Rendering it while the page is gated off would give
  // every one of its links — the header, each row, "View all" — a destination
  // `FeatureRoute` bounces straight back to /dashboard.
  const showOpenSearches = access.page("client-engagement", "dash-searches").visible;

  const visibleCards = homeCards().filter((card) => access.section(card.sectionId).visible);

  return (
    <div data-testid="dashboard" className="p-12">
      <h1 className="text-display text-text mb-10">
        Welcome, {me.data.first_name || fullName}
        <span className="text-accent">.</span>
      </h1>

      {/* Admin-only staff alerts — shown only when there are pending decisions. */}
      {isAdmin && <div className="mb-10"><StaffAlertsWidget /></div>}

      {/* Widget column left, lifecycle grid right. Below `lg` they stack, and
          the widget goes UNDER the grid (`order-last`): the grid is what the
          dashboard is, and on a phone it should not be pushed off-screen by a
          shortcut panel.

          From `lg` up the widget is 40% of the row (Trello #853, was a fixed
          288px that truncated search names) and exactly as tall as the grid.
          The row stretches its children, and the widget is absolutely filled
          into its wrapper, so the GRID alone sets the height: twelve rows
          scroll inside the panel instead of making the row taller than the
          cards, and three rows leave the "View all" link at the bottom edge.
          `lg:min-h-80` is the floor for when the cards are few: a single row
          of two would otherwise leave room for barely two searches. The grid
          packs its rows to the top (`content-start`), so under that floor the
          cards keep their size and the extra height is empty space below.

          Without the panel the grid goes back to 3 × 2, centered and capped
          (`besidePanel`): two columns stretched across the whole row would
          make each card half the page wide. */}
      <div className="flex flex-col gap-6 lg:flex-row lg:items-stretch">
        {showOpenSearches ? (
          <div className="order-last lg:relative lg:order-first lg:min-h-80 lg:w-2/5 lg:min-w-72 lg:shrink-0">
            <MyOpenSearchesWidget fillHeight className="lg:absolute lg:inset-0" />
          </div>
        ) : null}

        <DashboardGrid className="flex-1" besidePanel={showOpenSearches}>
          {visibleCards.map((card) => {
            // External shortcut wins over the dynamic resolver (the card is a
            // shortcut to an off-app tool, not a section entry point).
            const href = card.externalUrl ?? firstReleasedPageHref(card.sectionId, flags);
            // A section with no released child renders disabled — never a
            // dead click. Common in preview-only or wholly off sections.
            const disabled = href === null;
            return (
              <DashboardCard
                key={card.title}
                title={card.title}
                status={
                  access.previewing && !access.section(card.sectionId).released
                    ? "coming-soon"
                    : undefined
                }
                icon={card.icon}
                disabled={disabled}
                onActivate={
                  // The `href === null` second clause is logically redundant
                  // with `disabled` (which IS `href === null`) — kept solely
                  // to narrow `href` from `string | null` to `string` inside
                  // the closure so `navigate(href)` typechecks. Removing it
                  // ("the OR halves are equivalent, simplify") would
                  // re-introduce a TS error. Reviewer note from PR #241.
                  disabled || href === null
                    ? undefined
                    : () =>
                        card.externalUrl
                          ? window.open(card.externalUrl, "_blank", "noopener,noreferrer")
                          : navigate(href)
                }
              />
            );
          })}
        </DashboardGrid>
      </div>

      {/* "What's new in NU Assist" (2026-10-08): full width under the widget
          and the grid. Renders nothing once every slide's 30 days are up. */}
      <WhatsNewCarousel className="mt-10" />
    </div>
  );
}
